/**
 * Chaves da API do bot (spec 022) — Configurações → Integrações → API do bot.
 * GET/POST /bot-keys e POST /bot-keys/:id/revoke, só admin. A chave vem UMA
 * vez, na resposta do POST: a mutation a entrega à tela e ela não entra no
 * cache de query nenhuma (a lista nunca a tem).
 */
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { BotApiKey, BotApiKeyCreateBody, BotApiKeyCreated } from '@erp/shared';
import { api } from '@/lib/api-client';
import { errorMessage as genericErrorMessage } from '@/features/tickets/ticket-format';

export const BOT_KEYS_KEY = ['erp', 'bot-keys'] as const;

export function botKeysOptions() {
  return queryOptions({
    queryKey: BOT_KEYS_KEY,
    queryFn: async ({ signal }) => {
      const res = await api<BotApiKey[]>('/bot-keys', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function useCreateBotKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BotApiKeyCreateBody) => {
      const res = await api<BotApiKeyCreated>('/bot-keys', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    // só a lista é invalidada: a chave em si fica no resultado da mutation, que
    // a tela descarta ao fechar o diálogo
    onSuccess: () => void qc.invalidateQueries({ queryKey: BOT_KEYS_KEY }),
  });
}

export function useRevokeBotKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api<BotApiKey>(`/bot-keys/${id}/revoke`, { method: 'POST' });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: BOT_KEYS_KEY }),
  });
}

const BOT_KEY_ERROR_LABELS: Record<string, string> = {
  forbidden: 'Apenas administradores gerenciam as chaves da API do bot.',
  bot_key_not_found: 'Chave não encontrada — atualize a página.',
  invalid_body: 'Dê um nome à chave e escolha ao menos um escopo.',
};

export function botKeyErrorMessage(err: unknown): string {
  if (err !== null && typeof err === 'object' && 'error' in err) {
    const code = (err as { error: unknown }).error;
    if (typeof code === 'string' && BOT_KEY_ERROR_LABELS[code]) return BOT_KEY_ERROR_LABELS[code];
  }
  return genericErrorMessage(err);
}
